import React, { useEffect, useState, useMemo } from "react";
import { db } from "./firebase";
import { collection, getDocs } from "firebase/firestore";
import "./Reports.css";

const PINNED_REP_ORDER = ["AA8", "DN", "MR5", "YK"];
const OTHER_REP_LABEL = "Other";

const sortRepsForStore = (repNames) => {
  const cleaned = repNames.filter(Boolean);

  return [...cleaned].sort((a, b) => {
    const aIsOther = a.trim().toLowerCase() === OTHER_REP_LABEL.toLowerCase();
    const bIsOther = b.trim().toLowerCase() === OTHER_REP_LABEL.toLowerCase();

    if (aIsOther && !bIsOther) return 1;
    if (!aIsOther && bIsOther) return -1;

    const aPinnedIndex = PINNED_REP_ORDER.indexOf(a);
    const bPinnedIndex = PINNED_REP_ORDER.indexOf(b);

    const aIsPinned = aPinnedIndex !== -1;
    const bIsPinned = bPinnedIndex !== -1;

    if (aIsPinned && bIsPinned) return aPinnedIndex - bPinnedIndex;
    if (aIsPinned && !bIsPinned) return -1;
    if (!aIsPinned && bIsPinned) return 1;

    return a.localeCompare(b);
  });
};

export default function Reports({
  selectedProfile,
  lockedRepCode,
  isStoreMode,
}) {
  const [reports, setReports] = useState([]);
  const [reps, setReps] = useState([]);
  const [repProfilesMap, setRepProfilesMap] = useState({});
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedRep, setSelectedRep] = useState(
    isStoreMode ? "ALL" : lockedRepCode || "ALL"
  );

  useEffect(() => {
    const fetchAll = async () => {
      const [reportsSnapshot, repsSnapshot, repProfilesSnapshot] =
        await Promise.all([
          getDocs(collection(db, "eodReports")),
          getDocs(collection(db, "reps")),
          getDocs(collection(db, "repProfiles")),
        ]);

      const reportsData = reportsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setReports(reportsData);

      const repsData = repsSnapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .map((rep) => rep.name)
        .filter(Boolean);

      setReps(sortRepsForStore(repsData));

      const profilesMap = {};
      repProfilesSnapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        profilesMap[docSnap.id] = {
          repCode: data.repCode || docSnap.id,
          displayName: (data.displayName || "").trim(),
        };
      });
      setRepProfilesMap(profilesMap);
    };

    fetchAll();
  }, []);

  useEffect(() => {
    if (isStoreMode) {
      setSelectedRep("ALL");
    } else {
      setSelectedRep(lockedRepCode || "ALL");
    }
  }, [isStoreMode, lockedRepCode, selectedProfile]);

  const groupedReports = useMemo(() => {
    const effectiveSelectedRep = isStoreMode ? selectedRep : lockedRepCode || "ALL";

    const filtered = reports
      .filter((r) => {
        const dateMatch = selectedDate ? r.date === selectedDate : true;
        const repMatch =
          effectiveSelectedRep === "ALL" ? true : r.rep === effectiveSelectedRep;
        return dateMatch && repMatch;
      })
      .sort((a, b) => {
        const dateTimeA = new Date(`${a.date} ${a.time || "00:00"}`);
        const dateTimeB = new Date(`${b.date} ${b.time || "00:00"}`);
        return dateTimeB - dateTimeA;
      });

    const grouped = filtered.reduce((acc, report) => {
      const key = `${report.date}__${report.time || "00:00"}`;

      if (!acc[key]) {
        acc[key] = {
          date: report.date,
          time: report.time || "",
          reps: [],
        };
      }

      acc[key].reps.push(report);
      return acc;
    }, {});

    return Object.values(grouped).sort((a, b) => {
      const dateTimeA = new Date(`${a.date} ${a.time || "00:00"}`);
      const dateTimeB = new Date(`${b.date} ${b.time || "00:00"}`);
      return dateTimeB - dateTimeA;
    });
  }, [reports, selectedDate, selectedRep, isStoreMode, lockedRepCode]);

  const getYesDPCount = (product) => {
    return (product?.dp || []).filter((entry) => entry?.value === "YES").length;
  };

  const getDisplayName = (repCode) => {
    if (!repCode) return "";
    const profile = repProfilesMap[repCode];
    return (profile?.displayName || repCode).trim();
  };

  // Build daily WhatsApp summary – same style as EODLogger buildSummary
  const buildDailySummaryForWhatsapp = () => {
    if (!selectedDate) return "";

    const effectiveSelectedRep = isStoreMode ? selectedRep : lockedRepCode || "ALL";

    const dailyReports = reports.filter((r) => {
      const dateMatch = r.date === selectedDate;
      const repMatch =
        effectiveSelectedRep === "ALL" ? true : r.rep === effectiveSelectedRep;
      return dateMatch && repMatch;
    });

    if (dailyReports.length === 0) return "";

    const repTotals = new Map(); // rep -> { totals, dpCount, aalCount }

    dailyReports.forEach((report) => {
      const repCode = report.rep;
      if (!repCode) return;

      if (!repTotals.has(repCode)) {
        repTotals.set(repCode, {
          totals: {},
          dpCount: 0,
          aalCount: 0,
        });
      }

      const repAgg = repTotals.get(repCode);

      (report.customers || []).forEach((cust) => {
        repAgg.aalCount += cust.aalCount || 0;

        (cust.products || []).forEach((p) => {
          if (p.qty > 0) {
            repAgg.totals[p.name] = (repAgg.totals[p.name] || 0) + p.qty;
          }

          (p.dp || []).forEach((entry) => {
            if (entry && entry.value === "YES") {
              repAgg.dpCount += 1;
            }
          });
        });
      });
    });

    if (repTotals.size === 0) return "";

    let summary = "";
    repTotals.forEach((agg, repCode) => {
      const entries = Object.entries(agg.totals);
      if (!entries.length && !agg.dpCount && !agg.aalCount) return;

      summary += `${repCode}: `;
      const textParts = entries.map(([name, qty]) => `${qty} ${name}`);
      if (agg.dpCount) textParts.push(`${agg.dpCount} DP`);
      if (agg.aalCount) textParts.push(`${agg.aalCount} AAL`);
      summary += textParts.join(", ") + "\n\n";
    });

    summary = summary.trim();
    if (!summary) return "";

    return `Date: ${selectedDate}\n\n${summary}`;
  };

  const handleSendDayToWhatsapp = () => {
    const finalText = buildDailySummaryForWhatsapp();

    if (!finalText) {
      alert("No data to send for this date.");
      return;
    }

    const encoded = encodeURIComponent(finalText);
    const url = `https://api.whatsapp.com/send?text=${encoded}`;
    window.open(url, "_blank");
  };

  return (
    <div className="reports-page">
      <div className="reports-shell">
        <h2 className="reports-title">Reports</h2>

        <div className="reports-filters">
          <input
            className="reports-input"
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          />

          {isStoreMode ? (
            <select
              className="reports-select"
              value={selectedRep}
              onChange={(e) => setSelectedRep(e.target.value)}
            >
              <option value="ALL">All Reps</option>
              {reps.map((code) => {
                const name = repProfilesMap[code]?.displayName?.trim();
                const label = name ? `${code} — ${name}` : code;
                return (
                  <option key={code} value={code}>
                    {label}
                  </option>
                );
              })}
            </select>
          ) : (
            <div className="reports-select reports-select-locked">
              Rep: {getDisplayName(lockedRepCode)}
            </div>
          )}
        </div>

        {isStoreMode && selectedDate && (
          <div style={{ marginBottom: 16 }}>
            <button
              className="whatsapp-btn"
              onClick={handleSendDayToWhatsapp}
            >
              Send day to WhatsApp
            </button>
          </div>
        )}

        <div className="reports-list">
          {groupedReports.length === 0 ? (
            <div className="reports-empty">No reports found.</div>
          ) : (
            groupedReports.map((group, i) => (
              <div key={i} className="report-card">
                <div className="report-header">
                  <strong className="report-rep">Report</strong>
                  <span className="report-meta">
                    {group.date} {group.time || ""}
                  </span>
                </div>

                <div className="report-customers">
                  {group.reps.map((repBlock, repIndex) => {
                    const customersWithItems =
                      repBlock.customers?.filter((c) => {
                        const items = c.products || [];

                        const hasQtyItems = items.some((p) => p.qty > 0);
                        const hasYesDP = items.some(
                          (p) => getYesDPCount(p) > 0
                        );

                        return hasQtyItems || hasYesDP;
                      }) || [];

                    if (customersWithItems.length === 0) return null;

                    const repDisplayName = getDisplayName(repBlock.rep);

                    return (
                      <div key={repIndex} className="rep-group-block">
                        <div className="rep-group-title">
                          {repDisplayName}
                        </div>

                        {customersWithItems.map((cust, ci) => {
                          const items =
                            cust.products?.filter((p) => p.qty > 0) || [];

                          const dpRows =
                            cust.products
                              ?.map((p) => {
                                const yesDPCount = getYesDPCount(p);
                                if (yesDPCount === 0) return null;

                                return {
                                  label: `${p.name} DP`,
                                  qty: yesDPCount,
                                };
                              })
                              .filter(Boolean) || [];

                          if (items.length === 0 && dpRows.length === 0)
                            return null;

                          return (
                            <div key={ci} className="customer-report-block">
                              <div className="customer-report-name">
                                {cust.name || `Customer ${ci + 1}`}
                              </div>

                              <div className="customer-items">
                                {items.map((p, idx) => (
                                  <div
                                    key={`item-${idx}`}
                                    className="customer-item-row"
                                  >
                                    <span className="item-qty">{p.qty}</span>
                                    <span className="item-name">{p.name}</span>
                                  </div>
                                ))}

                                {dpRows.map((dpRow, idx) => (
                                  <div
                                    key={`dp-${idx}`}
                                    className="customer-item-row dp-report-row"
                                  >
                                    <span className="item-qty">
                                      {dpRow.qty}
                                    </span>
                                    <span className="item-name">
                                      {dpRow.label}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}